import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

function Signup() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  // Separate from `form` because it is not a value being collected — it is a gate. Keeping it out
  // of the same object makes it impossible to submit the form by spreading state and forgetting it.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleStatus, setGoogleStatus] = useState('loading');
  const googleBtnRef = useRef(null);
  const googleRenderedRef = useRef(false);
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '765799705459-kd3p7qee43a056lc9tdprmp223svtgkr.apps.googleusercontent.com';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    // Checked here as well as by the `required` attribute, because a disabled submit button and a
    // browser validation message are both things a determined form-filler can get around, and the
    // server records this as consent.
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions and Privacy Policy to continue.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/signup`, {
        name: form.name,
        email: form.email,
        password: form.password,
        acceptedTerms: true,
      });
      if (response.data?.token) {
        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('userProfile', JSON.stringify(response.data.user));
        window.dispatchEvent(new Event('auth-changed'));
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setError('');
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, {
        idToken: credential,
      });
      if (response.data?.token) {
        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('userProfile', JSON.stringify(response.data.user));
        window.dispatchEvent(new Event('auth-changed'));
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let intervalId;
    let timeoutId;
    const initGoogleButton = () => {
      if (!window.google || !googleBtnRef.current) return false;
      if (googleRenderedRef.current) return true;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          if (response?.credential) {
            handleGoogleCredential(response.credential);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signup_with',
        shape: 'pill',
        width: Math.min(320, Math.max(220, window.innerWidth - 72)),
      });
      googleRenderedRef.current = true;
      setGoogleStatus('ready');
      return true;
    };

    if (!initGoogleButton()) {
      intervalId = setInterval(() => {
        if (initGoogleButton()) {
          clearInterval(intervalId);
        }
      }, 300);
      timeoutId = setTimeout(() => {
        if (!googleRenderedRef.current) {
          setGoogleStatus('unavailable');
          if (intervalId) clearInterval(intervalId);
        }
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [googleClientId, handleGoogleCredential]);

  return (
    <div className="auth-page page-shell">
      <div className="auth-card section-card">
        <span className="tag">Create account</span>
        <h1 className="page-title">Join the Vix Algo community</h1>
        <p className="page-lead">
          Unlock license management, purchase history, and direct support messaging.
        </p>

        <div className={`google-signin-shell ${googleStatus}`}>
          <div ref={googleBtnRef} className="google-button-slot"></div>
          {googleStatus === 'loading' && <p>Loading Google sign-in...</p>}
          {googleStatus === 'unavailable' && (
            <p>Google sign-in could not load.</p>
          )}
        </div>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-group">
            <label htmlFor="signup-name">Full name</label>
            <input
              id="signup-name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="Your name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@domain.com"
              required
            />
          </div>
          <div className="form-grid two-column">
            <div className="form-group">
              <label htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Create password"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signup-confirm-password">Confirm password</label>
              <input
                id="signup-confirm-password"
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat password"
                required
              />
            </div>
          </div>
          <label className="auth-consent">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              required
            />
            <span>
              I have read and accept the{' '}
              {/* Opened in a new tab so reading them does not throw away a half-filled form. */}
              <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</Link>
              {' '}and the{' '}
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
            </span>
          </label>
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" disabled={isLoading || !acceptedTerms}>
            {isLoading ? 'Creating...' : 'Create Account'}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
